/* eslint-disable @cspell/spellchecker */
import styled, { createGlobalStyle, css } from "styled-components";

// Interstate Components primary blue color - matches the design system
const INTERSTATE_PRIMARY_BLUE = "#007bff";
const INTERSTATE_PRIMARY_BLUE_LIGHT = "rgba(0, 123, 255, 0.1)";
const INTERSTATE_PRIMARY_BLUE_BORDER = "rgba(0, 123, 255, 0.3)";

// Common styles for Interstate blue selection styling
const interstateSelectionStyles = css`
  background-color: ${INTERSTATE_PRIMARY_BLUE_LIGHT} !important;
  background-image: none !important;
  border-color: ${INTERSTATE_PRIMARY_BLUE_BORDER} !important;
  color: ${INTERSTATE_PRIMARY_BLUE} !important;
`;

const interstateHoverStyles = css`
  background-color: ${INTERSTATE_PRIMARY_BLUE_LIGHT} !important;
  background-image: none !important;
  color: ${INTERSTATE_PRIMARY_BLUE} !important;
`;

/**
 * Global styles for Kendo Editor dropdowns that render outside the component tree
 * These styles will override Kendo's default pink/red selection colors globally
 */
export const KendoEditorGlobalStyles = createGlobalStyle`
  /* Global overrides for Kendo Editor dropdown selections */
  .k-popup .k-list-item.k-selected,
  .k-popup .k-list-item.k-state-selected {
    ${interstateSelectionStyles}
  }

  /* Global hover states for Kendo Editor dropdowns */
  .k-popup .k-list-item:hover {
    ${interstateHoverStyles}
  }

  /* Ensure Kendo popups don't appear above modal overlays */
  .k-popup,
  .k-tooltip,
  .k-animation-container {
    z-index: 10000 !important; /* Lower than GenerativeAI modal (10100) */
  }

  /* Ensure ViewHtml dialog/modal appears above everything else in editor context */
  .k-dialog,
  .k-window,
  .k-dialog-wrapper,
  .k-window-wrapper {
    z-index: 10050 !important; /* Above stationery dropdowns but below GenerativeAI modal */
  }

  /* Custom styling for format block icon alignment */
  .custom-format-block-aligned {
    .k-button-icon,
    .k-svg-icon,
    .k-icon {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-end !important; /* Right align the icon */
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    .k-button-text {
      display: none !important; /* Hide any text to show only icon */
    }
  }

  /* Ensure ViewHtml modal content is properly layered */
  .k-overlay {
    z-index: 10040 !important;
  }

  /* Stationery dropdown ellipsis handling for popup items */
  .k-animation-container[data-stationery-dropdown] .k-list-item,
  .k-animation-container[data-stationery-dropdown] .k-list-item .k-list-item-text,
  .k-animation-container[data-stationery-dropdown] .k-list-item span {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 175px !important;
    width: 175px !important;
    display: block !important;
  }

  /* Stationery dropdown popup container width */
  .k-animation-container[data-stationery-dropdown] .k-list {
    width: 180px !important;
    max-width: 180px !important;
  }

  /* Stationery dropdown popup hover styles */
  .k-animation-container[data-stationery-dropdown] .k-list-item:hover {
    background-color: #f0f0f0 !important;
    cursor: pointer !important;
  }

  /* Text highlighting styles - Interstate blue background with white text */
  .k-editor-content ::selection,
  .k-editor-content ::-moz-selection {
    background-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    color: #ffffff !important;
  }

  /* Kendo Editor highlight tool styling */
  .k-editor-content .k-highlight,
  .k-editor-content mark,
  .k-editor-content .ProseMirror-selected,
  .k-editor-content .ProseMirror .ProseMirror-selectednode {
    background-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    color: #ffffff !important;
  }

  /* ViewHtml modal Update button styling */
  .k-dialog .k-button.k-button-md.k-rounded-md.k-button-solid.k-button-solid-primary,
  .k-window .k-button.k-button-md.k-rounded-md.k-button-solid.k-button-solid-primary,
  .k-dialog .k-primary,
  .k-window .k-primary {
    background-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    border-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    color: #ffffff !important;
  }

  /* ViewHtml modal Update button hover state */
  .k-dialog .k-button.k-button-md.k-rounded-md.k-button-solid.k-button-solid-primary:hover,
  .k-window .k-button.k-button-md.k-rounded-md.k-button-solid.k-button-solid-primary:hover,
  .k-dialog .k-primary:hover,
  .k-window .k-primary:hover {
    background-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    border-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    color: #ffffff !important;
    opacity: 0.9;
  }

  /* ViewHtml modal Update button active/pressed state */
  .k-dialog .k-button.k-button-md.k-rounded-md.k-button-solid.k-button-solid-primary:active,
  .k-window .k-button.k-button-md.k-rounded-md.k-button-solid.k-button-solid-primary:active,
  .k-dialog .k-primary:active,
  .k-window .k-primary:active {
    background-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    border-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    color: #ffffff !important;
    opacity: 0.8;
  }

  /* Override CSS custom properties globally for Kendo dropdowns */
  .k-popup {
    --kendo-color-primary: ${INTERSTATE_PRIMARY_BLUE};
    --kendo-color-primary-hover: ${INTERSTATE_PRIMARY_BLUE};
    --kendo-color-primary-active: ${INTERSTATE_PRIMARY_BLUE};
    --kendo-color-on-primary: #ffffff;
    --kendo-color-primary-subtle: ${INTERSTATE_PRIMARY_BLUE_LIGHT};
    --kendo-color-primary-subtle-hover: ${INTERSTATE_PRIMARY_BLUE_LIGHT};
    --kendo-color-primary-subtle-active: ${INTERSTATE_PRIMARY_BLUE_LIGHT};
  }

  /* Override CSS custom properties for dialogs and windows */
  .k-dialog,
  .k-window {
    --kendo-color-primary: ${INTERSTATE_PRIMARY_BLUE};
    --kendo-color-primary-hover: ${INTERSTATE_PRIMARY_BLUE};
    --kendo-color-primary-active: ${INTERSTATE_PRIMARY_BLUE};
    --kendo-color-on-primary: #ffffff;
  }

  /* Force interstate blue selection styling with maximum specificity */
  .k-editor-content,
  .k-editor-content *,
  .k-editor-content div,
  .k-editor-content p,
  .k-editor-content span,
  .k-editor-content .ProseMirror,
  .k-editor-content .k-content {
    &::selection {
      background-color: ${INTERSTATE_PRIMARY_BLUE} !important;
      color: #ffffff !important;
    }
    &::-moz-selection {
      background-color: ${INTERSTATE_PRIMARY_BLUE} !important;
      color: #ffffff !important;
    }
  }
`;

/**
 * Styled wrapper for the Kendo Editor component
 * Provides comprehensive styling for all editor states and interactions
 */
export const StyledKendoEditor = styled.div<{ $isDisabled?: boolean }>`
  /* Add custom styling for the Kendo Editor wrapper */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  /* Override any local dropdown selections with Interstate blue */
  .k-list-item.k-selected,
  .k-list-item.k-state-selected {
    ${interstateSelectionStyles}
  }

  .k-list-item:hover {
    ${interstateHoverStyles}
  }

  /* Dropdown focus styling */
  .k-dropdown.k-state-focused,
  .k-dropdown.k-state-active {
    border-color: ${INTERSTATE_PRIMARY_BLUE} !important;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
  }

  .k-editor {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    border-radius: 4px;
    border: 1px solid #ccc;
    transition: opacity 0.2s ease-in-out;

    ${props =>
      props.$isDisabled &&
      css`
        opacity: 0.6;
        pointer-events: none;
        background-color: #f5f5f5;
        cursor: not-allowed;
      `}
  }

  .k-editor-toolbar {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;

    ${props =>
      props.$isDisabled &&
      css`
        background-color: #e9ecef;
        opacity: 0.7;
      `}
  }

  .k-editor-content {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    min-height: calc(4 * 1.5em) !important;
    /* Height will be set dynamically via inline styles */
    padding: 12px;
    overflow-y: auto; /* Enable scrolling when content exceeds height */
    overflow-x: hidden; /* Default: no horizontal scroll. Toggled to 'auto' dynamically when content overflows (e.g., wide templates/images) */
    resize: none; /* Prevent manual resizing */
    cursor: text; /* Show text cursor when hovering over the entire editor area */

    ${props =>
      props.$isDisabled &&
      css`
        background-color: #e9ecef !important;
        color: #495057 !important;
        cursor: not-allowed;
      `}
  }

  /* Override inline styles applied by Kendo Editor */
  .k-editor-content div[style*="height"] {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    min-height: calc(4 * 1.5em - 24px) !important;
    max-height: none !important; /* Remove height constraints on content div */
    height: auto !important;
    overflow-x: hidden !important;
    overflow-y: visible !important; /* Let parent handle scrolling */
  }

  .k-editor-content p {
    margin: 0;
    padding: 0;
  }

  /*
   * Override Kendo's default table styles that break email template rendering.
   * Kendo applies table-layout:fixed, width:100%, border:1px solid, and padding
   * to all tables/cells inside the editor, which destroys the template's
   * explicit column widths, adds unwanted borders, and overrides padding.
   * These resets let the template's own inline styles take effect.
   */
  .k-editor-content table {
    table-layout: auto !important;
    width: auto !important;
    overflow: visible !important;
    white-space: normal !important;
    max-width: 100% !important;
  }

  /* Below 768px, allow horizontal scroll instead of squeezing */
  @media (max-width: 768px) {
    .k-editor-content table {
      max-width: none !important;
    }
  }

  .k-editor-content td,
  .k-editor-content th {
    padding: 0 !important;
    vertical-align: middle !important;
  }

  /* Ensure the editor content respects width and height constraints */
  .k-editor-content .ProseMirror,
  .k-editor-content .k-content {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    min-height: 100% !important; /* Fill the entire editor content area */
    max-height: none !important; /* Allow content to flow naturally */
    height: 100% !important; /* Take full height of parent container */
    overflow: visible !important; /* Content flows naturally, parent handles scroll */
    cursor: text; /* Ensure text cursor appears throughout the entire area */
  }

  /* Make content area greyed when disabled */
  ${props =>
    props.$isDisabled &&
    css`
      .k-editor-content .k-content {
        background-color: #e9ecef !important;
        color: #495057 !important;
      }
    `}

  /* Custom focus styles */
  .k-editor.k-state-focused {
    ${props =>
      !props.$isDisabled &&
      css`
        border-color: ${INTERSTATE_PRIMARY_BLUE};
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      `}
  }

  /* Toolbar button styling */
  &&& .k-toolbar .k-button {
    border: none;
    background: transparent;
    transition: background-color 0.15s ease-in-out;

    ${props =>
      !props.$isDisabled &&
      css`
        &:hover {
          background-color: #e9ecef !important;
          color: #212529 !important;
        }

        &.k-state-active,
        &.k-state-active:hover,
        &.k-selected,
        &.k-selected:hover {
          background-color: #e9ecef !important;
          background-image: none !important;
          color: #212529 !important;
          border-color: transparent !important;
          box-shadow: none !important;
        }

        &.k-state-active .k-icon,
        &.k-state-active .k-svg-icon,
        &.k-state-active span,
        &.k-selected .k-icon,
        &.k-selected .k-svg-icon,
        &.k-selected span {
          color: #212529 !important;
          fill: #212529 !important;
          stroke: #212529 !important;
        }
      `}

    ${props =>
      props.$isDisabled &&
      css`
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      `}
  }

  /* Disabled state pseudo-element: hide info icon entirely */
  ${props =>
    props.$isDisabled &&
    css`
      &::after {
        content: none !important;
        display: none !important;
      }
    `}

  /* Custom ordered list numbering styles */
  .k-editor-content {
    ol {
      list-style-type: decimal;

      ol {
        list-style-type: lower-alpha;

        ol {
          list-style-type: lower-roman;
        }
      }
    }
  }
`;
